/**
 * @zhxt/coord-datum — 坐标基准纯算法（无依赖，前后端共用）
 *
 * 支持 WGS84、GCJ02（国测局）、BD09（百度）、CGCS2000（按 WGS84 近似）、BD09MC（百度墨卡托）→ BD09LL。
 * 业务约定：数据一律存 WGS84；纠偏只在显示层（前端 basemapCoord.toDisplay / fromDisplay）发生。
 *
 * 唯一源文件：shared/coord-datum/index.js
 * 前端同步副本：frontend/src/utils/coordDatum/coordDatum.js（CRA 不能 import src 外文件，前端 Docker 构建上下文也只有 frontend/）
 *   修改本文件后运行：node backend/scripts/sync-coord-datum.js
 *   回归：cd backend && npm run test:coord（含副本一致性检查）
 *
 * 写法约束：CommonJS、只用 ES2015 语法且不用对象展开（前端副本经 Babel 编译，不得引入运行时 helper）。
 * 前半部分 isInChina … convert 由 backend/src/utils/coordinateConverter.js 逐字搬来（P1，行为不变），
 * 对 number 以外的输入不做转换，保持原后端语义；新代码请用带校验的 convertLngLat。
 */

// GCJ02 偏移参数
var GCJ02_A = 6378245.0;
var GCJ02_EE = 0.00669342162296594323;

// BD09 偏移参数
var BD09_X_PI = Math.PI * 3000.0 / 180.0;

/** 坐标基准枚举 */
var DATUM = Object.freeze({
  WGS84: 'WGS84',
  GCJ02: 'GCJ02',
  BD09: 'BD09',
  CGCS2000: 'CGCS2000'
});

// 判断坐标是否在中国境内
function isInChina(lng, lat) {
  // 中国大陆边界范围（更准确的范围）
  return lng >= 73.66 && lng <= 135.05 && lat >= 3.86 && lat <= 53.55;
}

// 计算GCJ02偏移量
function transformLat(x, y) {
  let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x));
  ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin(y / 3.0 * Math.PI)) * 2.0 / 3.0;
  ret += (160.0 * Math.sin(y / 12.0 * Math.PI) + 320 * Math.sin(y * Math.PI / 30.0)) * 2.0 / 3.0;
  return ret;
}

function transformLng(x, y) {
  let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x));
  ret += (20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) * 2.0 / 3.0;
  ret += (20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin(x / 3.0 * Math.PI)) * 2.0 / 3.0;
  ret += (150.0 * Math.sin(x / 12.0 * Math.PI) + 300.0 * Math.sin(x / 30.0 * Math.PI)) * 2.0 / 3.0;
  return ret;
}

// WGS84 转 GCJ02
function wgs84ToGcj02(lng, lat) {
  if (!isInChina(lng, lat)) {
    return { lng, lat };
  }

  let dLat = transformLat(lng - 105.0, lat - 35.0);
  let dLng = transformLng(lng - 105.0, lat - 35.0);

  const radLat = lat / 180.0 * Math.PI;
  let magic = Math.sin(radLat);
  magic = 1 - GCJ02_EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);

  dLat = (dLat * 180.0) / ((GCJ02_A * (1 - GCJ02_EE)) / (magic * sqrtMagic) * Math.PI);
  dLng = (dLng * 180.0) / (GCJ02_A / sqrtMagic * Math.cos(radLat) * Math.PI);

  return {
    lng: lng + dLng,
    lat: lat + dLat
  };
}

// GCJ02 转 WGS84（迭代逼近，减小单次反算误差；iterations 缺省 5 轮，与原后端一致）
function gcj02ToWgs84(lng, lat, iterations) {
  if (!isInChina(lng, lat)) {
    return { lng, lat };
  }

  const rounds = iterations === undefined ? 5 : iterations;
  let wgsLng = lng;
  let wgsLat = lat;
  for (let i = 0; i < rounds; i += 1) {
    const gcj = wgs84ToGcj02(wgsLng, wgsLat);
    wgsLng += lng - gcj.lng;
    wgsLat += lat - gcj.lat;
  }
  return { lng: wgsLng, lat: wgsLat };
}

// GCJ02 转 BD09
function gcj02ToBd09(lng, lat) {
  const z = Math.sqrt(lng * lng + lat * lat) + 0.00002 * Math.sin(lat * BD09_X_PI);
  const theta = Math.atan2(lat, lng) + 0.000003 * Math.cos(lng * BD09_X_PI);
  const bdLng = z * Math.cos(theta) + 0.0065;
  const bdLat = z * Math.sin(theta) + 0.006;
  return { lng: bdLng, lat: bdLat };
}

// BD09 转 GCJ02
function bd09ToGcj02(lng, lat) {
  const x = lng - 0.0065;
  const y = lat - 0.006;
  const z = Math.sqrt(x * x + y * y) - 0.00002 * Math.sin(y * BD09_X_PI);
  const theta = Math.atan2(y, x) - 0.000003 * Math.cos(x * BD09_X_PI);
  const ggLng = z * Math.cos(theta);
  const ggLat = z * Math.sin(theta);
  return { lng: ggLng, lat: ggLat };
}

// WGS84 转 BD09
function wgs84ToBd09(lng, lat) {
  const gcj02 = wgs84ToGcj02(lng, lat);
  return gcj02ToBd09(gcj02.lng, gcj02.lat);
}

// BD09 转 WGS84
function bd09ToWgs84(lng, lat) {
  const gcj02 = bd09ToGcj02(lng, lat);
  return gcj02ToWgs84(gcj02.lng, gcj02.lat);
}

// CGCS2000 转 WGS84 (近似，实际差异很小)
function cgcs2000ToWgs84(lng, lat) {
  // CGCS2000与WGS84差异很小，通常可以近似相等
  return { lng, lat };
}

// WGS84 转 CGCS2000
function wgs84ToCgcs2000(lng, lat) {
  // CGCS2000与WGS84差异很小，通常可以近似相等
  return { lng, lat };
}

// 百度墨卡托(BD09MC)转百度经纬度(BD09LL)
// 采用百度 JS API MercatorProjection 同款分段多项式（不可用 Web Mercator 3857）
function bd09MercatorToLngLat(mercatorX, mercatorY) {
  const absX = Math.abs(mercatorX);
  const absY = Math.abs(mercatorY);
  if (absX <= 180 && absY <= 90) {
    return { lng: mercatorX, lat: mercatorY };
  }

  const MCBAND = [12890594.86, 8362377.87, 5591021, 3481989.83, 1678043.12, 0];
  // 每项 10 个数：最后一位为纬度缩放分母（百度 API / 开源 MC2LL 完整表）
  const MC2LL = [
    [1.410526172116255e-8, 0.00000898305509648872, -1.9939833816331, 200.9824383106796, -187.2403703815547, 91.6087516669843, -23.38765649603339, 2.57121317296198, -0.03801003308653, 17337981.2],
    [-7.435856389565537e-9, 0.000008983055097726239, -0.78625201886289, 96.32687599759846, -1.85204757529826, -59.36935905485877, 47.40033549296737, -16.50741931063887, 2.28786674699375, 10260144.86],
    [-3.030883460898826e-8, 0.00000898305509983578, 0.30071316287616, 59.74293618442277, 7.357984074871, -25.38371002664745, 13.45380521110908, -3.29883767235584, 0.32710905363475, 6856817.37],
    [-1.981981304930552e-8, 0.000008983055099779535, 0.03278182852591, 40.31678527705744, 0.65659298677277, -4.44255534477492, 0.85341911805263, 0.12923347998204, -0.04625736007561, 4482777.06],
    [3.09191371068437e-9, 0.000008983055096812155, 0.00006995724062, 23.10934304144901, -0.00023663490511, -0.6321817810242, -0.00663494467273, 0.03430082397953, -0.00466043876332, 2555164.4],
    [2.890871144776878e-9, 0.000008983055095805407, -3.068298e-8, 7.47137025468032, -0.00000353937994, -0.02145144861037, -0.00001234426596, 0.00010322952773, -0.00000323890364, 826088.5]
  ];

  let factor = null;
  for (let i = 0; i < MCBAND.length; i += 1) {
    if (absY >= MCBAND[i]) {
      factor = MC2LL[i];
      break;
    }
  }
  if (!factor) {
    throw new Error(`无效的百度墨卡托纬度分量: ${mercatorY}`);
  }

  let lng = factor[0] + factor[1] * absX;
  const t = absY / factor[9];
  let lat = factor[2] + factor[3] * t + factor[4] * t * t
    + factor[5] * t ** 3 + factor[6] * t ** 4
    + factor[7] * t ** 5 + factor[8] * t ** 6;
  lng *= mercatorX < 0 ? -1 : 1;
  lat *= mercatorY < 0 ? -1 : 1;
  return { lng, lat };
}

// 通用转换方法（原后端语义：坐标系名不区分大小写，不支持时抛错）
function convert(lng, lat, fromSystem, toSystem) {
  // 先转换为WGS84
  let wgs84 = { lng, lat };

  switch (fromSystem.toUpperCase()) {
    case 'WGS84':
      wgs84 = { lng, lat };
      break;
    case 'GCJ02':
      wgs84 = gcj02ToWgs84(lng, lat);
      break;
    case 'BD09':
      wgs84 = bd09ToWgs84(lng, lat);
      break;
    case 'CGCS2000':
      wgs84 = cgcs2000ToWgs84(lng, lat);
      break;
    default:
      throw new Error(`不支持的源坐标系: ${fromSystem}`);
  }

  // 再从WGS84转换为目标坐标系
  switch (toSystem.toUpperCase()) {
    case 'WGS84':
      return wgs84;
    case 'GCJ02':
      return wgs84ToGcj02(wgs84.lng, wgs84.lat);
    case 'BD09':
      return wgs84ToBd09(wgs84.lng, wgs84.lat);
    case 'CGCS2000':
      return wgs84ToCgcs2000(wgs84.lng, wgs84.lat);
    default:
      throw new Error(`不支持的目标坐标系: ${toSystem}`);
  }
}

/* ===================== 以下为新增的带校验接口（P1 新增，供新代码使用） ===================== */

var DATUM_ALIASES = {
  WGS84: DATUM.WGS84,
  'WGS-84': DATUM.WGS84,
  EPSG4326: DATUM.WGS84,
  'EPSG:4326': DATUM.WGS84,
  GCJ02: DATUM.GCJ02,
  'GCJ-02': DATUM.GCJ02,
  GCJ: DATUM.GCJ02,
  BD09: DATUM.BD09,
  'BD-09': DATUM.BD09,
  BD09LL: DATUM.BD09,
  CGCS2000: DATUM.CGCS2000
};

/** 坐标系名称归一（不认识返回 null，不抛错） */
function normalizeDatum(name) {
  if (name === null || name === undefined) return null;
  const key = String(name).trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(DATUM_ALIASES, key) ? DATUM_ALIASES[key] : null;
}

function toWgs84Internal(lng, lat, from, iterations) {
  switch (from) {
    case DATUM.WGS84:
    case DATUM.CGCS2000:
      return { lng, lat };
    case DATUM.GCJ02:
      return gcj02ToWgs84(lng, lat, iterations);
    case DATUM.BD09: {
      const g = bd09ToGcj02(lng, lat);
      return gcj02ToWgs84(g.lng, g.lat, iterations);
    }
    default:
      return null;
  }
}

function fromWgs84Internal(lng, lat, to) {
  switch (to) {
    case DATUM.WGS84:
    case DATUM.CGCS2000:
      return { lng, lat };
    case DATUM.GCJ02:
      return wgs84ToGcj02(lng, lat);
    case DATUM.BD09:
      return wgs84ToBd09(lng, lat);
    default:
      return null;
  }
}

/**
 * 带校验的通用转换：入参转 Number；非有限值或坐标系不认识返回 null；同基准原样返回。
 * options.iterations：GCJ→WGS 反算轮数（默认 5；显示层拖拽/DEM 可用 2）。
 * @returns {{lng:number, lat:number}|null}
 */
function convertLngLat(lng, lat, from, to, options) {
  const lngN = Number(lng);
  const latN = Number(lat);
  if (!Number.isFinite(lngN) || !Number.isFinite(latN)) return null;
  const f = normalizeDatum(from);
  const t = normalizeDatum(to);
  if (!f || !t) return null;
  if (f === t) return { lng: lngN, lat: latN };
  let iterations = options && options.iterations !== undefined ? Number(options.iterations) : 5;
  if (!Number.isFinite(iterations)) iterations = 5;
  iterations = Math.max(1, iterations);
  const wgs = toWgs84Internal(lngN, latN, f, iterations);
  if (!wgs) return null;
  return fromWgs84Internal(wgs.lng, wgs.lat, t);
}

/** [lng, lat] 版本，失败返回 null */
function convertPair(lng, lat, from, to, options) {
  const r = convertLngLat(lng, lat, from, to, options);
  return r ? [r.lng, r.lat] : null;
}

module.exports = {
  DATUM,
  GCJ02_A,
  GCJ02_EE,
  BD09_X_PI,
  isInChina,
  transformLat,
  transformLng,
  wgs84ToGcj02,
  gcj02ToWgs84,
  gcj02ToBd09,
  bd09ToGcj02,
  wgs84ToBd09,
  bd09ToWgs84,
  cgcs2000ToWgs84,
  wgs84ToCgcs2000,
  bd09MercatorToLngLat,
  convert,
  normalizeDatum,
  convertLngLat,
  convertPair
};